import { Injectable } from '@angular/core';
import { ValidatorFn, AbstractControl, ValidationErrors, FormGroup, Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class ActivityValidationsService {

 constructor() { }

  // Validator for checking if date is not in the future
  notFutureDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const inputDate = new Date(control.value);
      inputDate.setHours(0, 0, 0, 0);
      
      return inputDate > today ? { futureDate: true } : null;
    };
  }

  // File size validator with type-specific limits
  validateFileSize(file: File): boolean {
    const fileType = file.type.toLowerCase();
    let maxSize: number;

    if (fileType.startsWith('image/')) {
      // Photos: 20MB limit
      maxSize = 20 * 1024 * 1024; // 20MB in bytes
    } else if (fileType.startsWith('video/')) {
      // Videos: 100MB limit
      maxSize = 100 * 1024 * 1024; // 100MB in bytes
    } else {
      // Other files: 20MB limit
      maxSize = 20 * 1024 * 1024; // 20MB in bytes
    }

    return file.size <= maxSize;
  }

  // Video duration validator (1 minute max)
validateVideoDuration(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) {
      resolve(true);
      return;
    }

    const reader = new FileReader();
    // Log file header (first bytes)
    reader.onload = (e: any) => {
      const buffer = e.target.result as ArrayBuffer;
      const bytes = new Uint8Array(buffer).subarray(0, 64);

      const blob = new Blob([buffer], { type: file.type });
      const blobUrl = URL.createObjectURL(blob);

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.src = blobUrl;

      video.onloadedmetadata = () => {

        URL.revokeObjectURL(blobUrl);

        resolve(video.duration <= 60);
      };

      video.load();
    };

    reader.onerror = (err) => {
      console.error("❌ FileReader Error:", err);
      resolve(false);
    };

    reader.readAsArrayBuffer(file);
  });
}

  

  // Apply validation based on activity type
  applyValidations(form: FormGroup, activityType: string): void {
    // Reset all validations first
    this.resetValidations(form);

    // Common validations for all activities
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]);
    form.get('time')?.setValidators([Validators.required]);
    
    // Activity specific validations
    switch(activityType) {
      case 'MEAL':
        this.applyMealValidations(form);
        break;
      case 'NAP':
        this.applyNapValidations(form);
        break;
      case 'BATHROOM':
        this.applyBathroomValidations(form);
        break;
      case 'BOTTLE':
        this.applyBottleValidations(form);
        break;
      case 'OUTDOOR':
        this.applyOutdoorValidations(form);
        break;
      case 'MEDICATION':
        this.applyMedicationValidations(form);
        break;
      case 'NOTES':
        this.applyNotesValidations(form);
        break;
      case 'OBSERVATION':
        this.applyObservationValidations(form);
        break;
      case 'INCIDENT':
        this.applyIncidentValidations(form);
        break;
      case 'LEARNING':
        this.applyLearningValidations(form);
        break;
      case 'PHOTO':
        // this.applyPhotoValidations(form);
        break;
    }
    
    // Update form validation
    form.updateValueAndValidity();
  }

  // Reset all validations
  private resetValidations(form: FormGroup): void {
    // Clear validators for all form controls
    Object.keys(form.controls).forEach(key => {
      form.get(key)?.clearValidators();
      form.get(key)?.updateValueAndValidity();
    });
  }

  // Meal Activity validations
  private applyMealValidations(form: FormGroup): void {
    form.get('activityRefValue1')?.setValidators([Validators.required]); // Meal Type
    form.get('activityRefValue2')?.setValidators([Validators.required]); // Quantity
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]); // Date with future check
    
    // Update validation status
    form.get('activityRefValue1')?.updateValueAndValidity();
    form.get('activityRefValue2')?.updateValueAndValidity();
    form.get('staffId')?.updateValueAndValidity();
    form.get('date')?.updateValueAndValidity();
  }

  // Nap Activity validations
  private applyNapValidations(form: FormGroup): void {    
    form.get('activityRefValue1')?.setValidators([Validators.required]); // Start Time
    // if(form.get('id')?.value != undefined){
    //   form.get('activityRefValue2')?.setValidators([Validators.required]); // End Time
    // }

    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    
    // Update validation status
    form.get('activityRefValue1')?.updateValueAndValidity();

    // if(form.get('id')?.value != undefined){
    // form.get('activityRefValue2')?.updateValueAndValidity();
    // }
    form.get('staffId')?.updateValueAndValidity();
  }

  // Bathroom Activity validations
  private applyBathroomValidations(form: FormGroup): void {
    form.get('students')?.setValidators([Validators.required]); // Students
    form.get('activityRefValue1')?.setValidators([Validators.required]); // Bathroom Type
    form.get('activityRefValue2')?.setValidators([Validators.required]); // Sub-Type
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    
    // Conditionally add validation for Diaper Size if Bathroom Type is 'Diaper'
    // if (form.get('activityRefValue1')?.value === 'Diaper') {
    //   form.get('activityRefValue3')?.setValidators([Validators.required]); // Diaper Size
    //   form.get('activityRefValue3')?.updateValueAndValidity();
    // }
    
    // Update validation status
    form.get('students')?.updateValueAndValidity();
    form.get('activityRefValue1')?.updateValueAndValidity();
    form.get('activityRefValue2')?.updateValueAndValidity();
    form.get('staffId')?.updateValueAndValidity();
  }

  // Bottle Activity validations
  private applyBottleValidations(form: FormGroup): void {
    // form.get('activityRefValue1')?.setValidators([Validators.required, Validators.min(0)]); // Amount (OZ)
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]); // Date with future check
    
    // Update validation status
    // form.get('activityRefValue1')?.updateValueAndValidity();
    form.get('staffId')?.updateValueAndValidity();
    form.get('date')?.updateValueAndValidity();
  }

  // Outdoor Activity validations
  private applyOutdoorValidations(form: FormGroup): void {
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]); // Date with future check
    // form.get('description')?.setValidators([Validators.required]); // Description
    
    // Update validation status
    form.get('staffId')?.updateValueAndValidity();
    form.get('date')?.updateValueAndValidity();
    // form.get('description')?.updateValueAndValidity();
  }

  // Medication Activity validations
  private applyMedicationValidations(form: FormGroup): void {
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('description')?.setValidators([Validators.required]); // Description
    
    // Update validation status
    form.get('staffId')?.updateValueAndValidity();
    form.get('description')?.updateValueAndValidity();
  }

  // Notes Activity validations
  private applyNotesValidations(form: FormGroup): void {
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('description')?.setValidators([Validators.required]); // Description
    
    // Update validation status
    form.get('staffId')?.updateValueAndValidity();
    form.get('description')?.updateValueAndValidity();
  }

  // Observation Activity validations
  private applyObservationValidations(form: FormGroup): void {
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('description')?.setValidators([Validators.required]); // Description
    
    // Update validation status
    form.get('staffId')?.updateValueAndValidity();
    form.get('description')?.updateValueAndValidity();
  }

  // Incident Activity validations
  private applyIncidentValidations(form: FormGroup): void {
    form.get('students')?.setValidators([Validators.required]); // Students
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('description')?.setValidators([Validators.required]); // What happened
    form.get('activityRefValue2')?.setValidators([Validators.required]); // First Aid toggle
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]); // Date with future check
    
    // Update validation status
    form.get('students')?.updateValueAndValidity();
    form.get('staffId')?.updateValueAndValidity();
    form.get('description')?.updateValueAndValidity();
    form.get('activityRefValue2')?.updateValueAndValidity();
    form.get('date')?.updateValueAndValidity();
  }

  // Learning Activity validations
  private applyLearningValidations(form: FormGroup): void {
    form.get('students')?.setValidators([Validators.required]); // Students
    form.get('activityRefValue1')?.setValidators([Validators.required]); // Activity Name
    form.get('activityRefValue2')?.setValidators([Validators.required]); // Category
    form.get('staffId')?.setValidators([Validators.required]); // Staff Present
    form.get('date')?.setValidators([Validators.required, this.notFutureDate()]); // Date with future check
    
    // Update validation status
    form.get('students')?.updateValueAndValidity();
    form.get('activityRefValue1')?.updateValueAndValidity();
    // form.get('activityRefValue2')?.updateValueAndValidity();
    form.get('staffId')?.updateValueAndValidity();
    form.get('date')?.updateValueAndValidity();
  }

  // Photo Activity validations
  private applyPhotoValidations(form: FormGroup): void {
    form.get('students')?.setValidators([Validators.required]); // Students
    // form.get('activityRefValue2')?.setValidators([Validators.required]); // Description/Remarks
    
    // Update validation status
    form.get('students')?.updateValueAndValidity();
    form.get('activityRefValue2')?.updateValueAndValidity();
  }
}